import React, { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { feriaPhotoThumbUrl } from "../../api/Ferias/FeriasApi";
import {
  FERIA_UNGROUPED_ID,
  applyFeriaBoardDrag,
  buildFeriaMediaBoard,
  photoHasLinkedMaterial,
} from "../../utils/feriaSessionUtils";
import {
  beginFeriaInboxPhotoDrag,
  clearFeriaInboxPhotoDrag,
  feriaSupplierDropFromPoint,
} from "./feriaPhotoDrag";
import FeriaDeletePhotoDialog from "./FeriaDeletePhotoDialog";

const isDesktopMediaFile = (file) => {
  const t = String(file?.type || "").toLowerCase();
  return t.startsWith("image/") || t.startsWith("video/");
};

const isVideo = (photo) => {
  const mime = String(photo?.mimeType || "").toLowerCase();
  return (
    photo?.kind === "video" ||
    mime.startsWith("video/") ||
    /\/videos\//i.test(String(photo?.fileUrl || ""))
  );
};

/** Thumb lazy (IntersectionObserver), como Meetings. */
function FeriaLazyThumb({ photo, video }) {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const src = feriaPhotoThumbUrl(photo);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "160px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [photo?.id]);

  if (!inView) {
    return (
      <Box
        ref={rootRef}
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: alpha("#0b1a24", 0.06),
        }}
      />
    );
  }

  if (src) {
    return (
      <Box
        ref={rootRef}
        component="img"
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          pointerEvents: "none",
        }}
      />
    );
  }

  return (
    <Stack
      ref={rootRef}
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%" }}
    >
      {video ? <VideocamOutlinedIcon color="action" /> : null}
    </Stack>
  );
}

/**
 * Media groups board for feria edición — con drag & drop entre grupos.
 */
const FeriaMediaGroupsBoard = ({
  photos = [],
  mediaGroups = [],
  selectedIds = [],
  onToggleSelect,
  onNewGroup,
  onMoveSelected,
  onDeleteSelected,
  /** Borrar una foto (dock: papelera + diálogo). */
  onDeletePhoto,
  /** Abrir carrusel (clic en foto, como Meetings). */
  onOpenMedia,
  onLinkMaterialoteca,
  onUnlinkMaterialoteca,
  onGroupsChange,
  onDesktopFilesDrop,
  onDropPhotoOnSupplier,
  onSupplierDragHover,
  /** Solo bandeja «Materiales» (sin columnas de grupos). */
  inboxOnly = false,
  busy = false,
}) => {
  const theme = useTheme();
  const [moveTarget, setMoveTarget] = useState("");
  const [desktopDropActive, setDesktopDropActive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const pointerRef = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const lastSupplierHitRef = useRef(null);
  const thumbPressRef = useRef({ x: 0, y: 0, id: null });

  useEffect(() => {
    // Solo para reorder hello-pangea (!inboxOnly). En dock nativo el
    // propio proveedor ilumina con dragenter (sin elementsFromPoint).
    if (inboxOnly) return undefined;
    let raf = 0;
    let lastSent = null;
    const onMove = (e) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      if (!draggingRef.current) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const hit = feriaSupplierDropFromPoint(
          pointerRef.current.x,
          pointerRef.current.y,
        );
        const nextId = hit?.supplierId ?? null;
        lastSupplierHitRef.current = nextId;
        if (nextId !== lastSent) {
          lastSent = nextId;
          onSupplierDragHover?.(nextId);
        }
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [onSupplierDragHover, inboxOnly]);

  const columns = useMemo(() => {
    const board = buildFeriaMediaBoard(photos, mediaGroups);
    if (!inboxOnly) return board;
    return board.filter((c) => c.ungrouped);
  }, [photos, mediaGroups, inboxOnly]);

  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => Number(id))),
    [selectedIds],
  );

  const namedGroups = columns.filter((c) => !c.ungrouped);

  const clearSupplierHover = () => {
    draggingRef.current = false;
    lastSupplierHitRef.current = null;
    onSupplierDragHover?.(null);
  };

  const resolveDropPhotoIds = (draggedId) => {
    const primary = Number(draggedId);
    if (!Number.isFinite(primary) || primary <= 0) return [];
    const selected = (selectedIds || [])
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0);
    // Si la foto arrastrada está en la selección → suelta todas las imágenes seleccionadas
    const bundle =
      selectedSet.has(primary) && selected.length > 1 ? selected : [primary];
    return bundle.filter((id) => {
      const p = (photos || []).find((ph) => Number(ph.id) === id);
      return p && !isVideo(p);
    });
  };

  const handleDragStart = () => {
    draggingRef.current = true;
    lastSupplierHitRef.current = null;
  };

  const tryDropOnSupplier = (result) => {
    if (!onDropPhotoOnSupplier || !result?.draggableId) return false;
    const hitNow = feriaSupplierDropFromPoint(
      pointerRef.current.x,
      pointerRef.current.y,
    );
    const supplierId = hitNow?.supplierId ?? lastSupplierHitRef.current ?? null;
    if (!supplierId) return false;
    const photoId = Number(String(result.draggableId).replace(/^photo-/, ""));
    const photo = (photos || []).find((p) => Number(p.id) === photoId);
    const photoIds = resolveDropPhotoIds(photoId);
    if (!photo || isVideo(photo) || !photoIds.length) return false;
    onDropPhotoOnSupplier(photo, supplierId, photoIds);
    return true;
  };

  const handleDragEnd = (result) => {
    // Suelta sobre proveedor (derecha o tarjeta central) aunque busy
    // esté true por otra operación — no bloquear el drop.
    if (!result.destination && tryDropOnSupplier(result)) {
      clearSupplierHover();
      return;
    }
    // También: destination dentro del dock pero el dedo está sobre un proveedor
    if (result.destination && tryDropOnSupplier(result)) {
      clearSupplierHover();
      return;
    }
    clearSupplierHover();
    if (busy) return;
    const nextGroups = applyFeriaBoardDrag(columns, result);
    if (nextGroups) onGroupsChange?.(nextGroups);
  };

  const handleDesktopDragOver = (e) => {
    if (busy || !onDesktopFilesDrop) return;
    const types = e.dataTransfer?.types;
    if (!types || ![...types].includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setDesktopDropActive(true);
  };

  const handleDesktopDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDesktopDropActive(false);
  };

  const handleDesktopDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDesktopDropActive(false);
    if (busy || !onDesktopFilesDrop) return;
    const list = Array.from(e.dataTransfer?.files || []).filter(
      isDesktopMediaFile,
    );
    if (list.length) onDesktopFilesDrop(list);
  };

  const confirmDeletePhoto = async () => {
    const id = Number(deleteTarget?.id);
    if (!Number.isFinite(id) || id <= 0 || !onDeletePhoto) return;
    setDeletingPhoto(true);
    try {
      await onDeletePhoto(id);
      setDeleteTarget(null);
    } finally {
      setDeletingPhoto(false);
    }
  };

  return (
    <Stack spacing={inboxOnly ? 0.75 : 1.5} sx={{ minHeight: 0, flex: 1 }}>
      {!inboxOnly ? (
        <>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
          >
            <Button
              size="small"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={onNewGroup}
              disabled={busy}
              sx={{ textTransform: "none" }}
            >
              Nuevo grupo
            </Button>
            <TextField
              select
              size="small"
              label="Mover a"
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              sx={{ minWidth: 160 }}
              disabled={busy || !selectedIds.length}
            >
              <MenuItem value={FERIA_UNGROUPED_ID}>Materiales</MenuItem>
              {namedGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.label || "Grupo"}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              startIcon={<DriveFileMoveOutlinedIcon />}
              disabled={busy || !selectedIds.length || !moveTarget}
              onClick={() => onMoveSelected?.(moveTarget)}
              sx={{ textTransform: "none" }}
            >
              Mover selección
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={busy || !selectedIds.length}
              onClick={onDeleteSelected}
              sx={{ textTransform: "none" }}
            >
              Eliminar
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<LinkIcon />}
              disabled={busy || !selectedIds.length}
              onClick={onLinkMaterialoteca}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Asignar a proveedor
            </Button>
            <Button
              size="small"
              startIcon={<LinkOffIcon />}
              disabled={busy || !selectedIds.length}
              onClick={onUnlinkMaterialoteca}
              sx={{ textTransform: "none" }}
            >
              Desvincular
            </Button>
            {selectedIds.length > 0 ? (
              <Chip
                size="small"
                label={`${selectedIds.length} seleccionada${
                  selectedIds.length === 1 ? "" : "s"
                }`}
              />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Selecciona una o varias fotos y arrástralas al proveedor (se
            ilumina).
          </Typography>
        </>
      ) : null}

      {inboxOnly ? (
        <Box
          onDragEnter={onDesktopFilesDrop ? handleDesktopDragOver : undefined}
          onDragOver={onDesktopFilesDrop ? handleDesktopDragOver : undefined}
          onDragLeave={onDesktopFilesDrop ? handleDesktopDragLeave : undefined}
          onDrop={onDesktopFilesDrop ? handleDesktopDrop : undefined}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha(theme.palette.text.primary, 0.14),
            bgcolor: "#fff",
            p: 1,
            outline: desktopDropActive
              ? `2px dashed ${theme.palette.primary.main}`
              : "none",
            outlineOffset: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
              gap: 0.75,
              minHeight: 72,
            }}
          >
            {(columns[0]?.photos || []).map((photo) => {
              const id = Number(photo.id);
              const selected = selectedSet.has(id);
              const video = isVideo(photo);
              const canDrag = Number.isFinite(id) && id > 0 && !video;
              return (
                <Box
                  key={id || photo._tempId}
                  data-feria-photo-id={id}
                  draggable={canDrag}
                  onDragStart={(e) => {
                    if (!canDrag) {
                      e.preventDefault();
                      return;
                    }
                    const bundle = resolveDropPhotoIds(id);
                    beginFeriaInboxPhotoDrag(e, bundle.length ? bundle : [id]);
                    e.currentTarget.style.opacity = "0.45";
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    clearFeriaInboxPhotoDrag();
                    onSupplierDragHover?.(null);
                  }}
                  onPointerDown={(e) => {
                    thumbPressRef.current = {
                      x: e.clientX,
                      y: e.clientY,
                      id,
                    };
                  }}
                  onClick={(e) => {
                    const press = thumbPressRef.current;
                    const dx = Math.abs((e.clientX || 0) - (press?.x || 0));
                    const dy = Math.abs((e.clientY || 0) - (press?.y || 0));
                    if (dx >= 8 || dy >= 8) return;
                    if (e.metaKey || e.ctrlKey) {
                      if (canDrag) onToggleSelect?.(id);
                      return;
                    }
                    if (onOpenMedia && Number.isFinite(id) && id > 0) {
                      onOpenMedia(id);
                    }
                  }}
                  sx={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: 1.25,
                    overflow: "hidden",
                    cursor: canDrag ? "grab" : "pointer",
                    border: "2px solid",
                    borderColor: selected
                      ? theme.palette.primary.main
                      : alpha(theme.palette.text.primary, 0.12),
                    bgcolor: alpha(theme.palette.text.primary, 0.06),
                    contain: "layout paint",
                    contentVisibility: "auto",
                    containIntrinsicSize: "72px 72px",
                    "&:active": { cursor: canDrag ? "grabbing" : "pointer" },
                  }}
                >
                  <FeriaLazyThumb photo={photo} video={video} />
                  {onDeletePhoto && canDrag && !photo._localPreview ? (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        aria-label="Eliminar foto"
                        disabled={busy || deletingPhoto}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setDeleteTarget(photo);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          width: 24,
                          height: 24,
                          bgcolor: alpha("#fff", 0.92),
                          color: "error.main",
                          "&:hover": { bgcolor: "#fff" },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {video ? (
                    <VideocamOutlinedIcon
                      sx={{
                        position: "absolute",
                        bottom: 3,
                        left: 3,
                        fontSize: 15,
                        color: "#fff",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,.6))",
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
            {!(columns[0]?.photos || []).length ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ gridColumn: "1 / -1", py: 2, px: 0.5 }}
              >
                {desktopDropActive
                  ? "Suelta para subir…"
                  : "Vacío — suelta fotos del escritorio o de la galería aquí"}
              </Typography>
            ) : null}
          </Box>
        </Box>
      ) : (
        <DragDropContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              overflow: "auto",
              flex: 1,
              minHeight: 0,
              pr: 0.5,
            }}
          >
            {columns.map((col) => {
              const acceptDesktop = Boolean(
                col.ungrouped && onDesktopFilesDrop,
              );
              return (
                <Box
                  key={col.id}
                  onDragEnter={
                    acceptDesktop ? handleDesktopDragOver : undefined
                  }
                  onDragOver={acceptDesktop ? handleDesktopDragOver : undefined}
                  onDragLeave={
                    acceptDesktop ? handleDesktopDragLeave : undefined
                  }
                  onDrop={acceptDesktop ? handleDesktopDrop : undefined}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.text.primary, 0.14),
                    p: 1.25,
                    outline:
                      acceptDesktop && desktopDropActive
                        ? `2px dashed ${theme.palette.primary.main}`
                        : "none",
                    outlineOffset: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    {col.ungrouped
                      ? "Materiales"
                      : col.label || "Grupo sin nombre"}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      ({col.photos?.length || 0})
                    </Typography>
                  </Typography>
                  <Droppable
                    droppableId={String(col.id)}
                    direction="horizontal"
                    isDropDisabled={busy}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(88px, 1fr))",
                          gap: 1,
                          minHeight: 96,
                          borderRadius: 1.5,
                          p: 0.5,
                          bgcolor:
                            snapshot.isDraggingOver ||
                            (acceptDesktop && desktopDropActive)
                              ? alpha(theme.palette.primary.main, 0.08)
                              : "transparent",
                        }}
                      >
                        {(col.photos || []).map((photo, index) => {
                          const id = Number(photo.id);
                          const selected = selectedSet.has(id);
                          const video = isVideo(photo);
                          const canDrag = Number.isFinite(id) && id > 0;
                          return (
                            <Draggable
                              key={id || photo._tempId}
                              draggableId={`photo-${id || photo._tempId}`}
                              index={index}
                              isDragDisabled={!canDrag}
                            >
                              {(dragProvided, dragSnapshot) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  data-feria-photo-id={id}
                                  sx={{
                                    position: "relative",
                                    aspectRatio: "1",
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    cursor: canDrag ? "grab" : "pointer",
                                    border: "2px solid",
                                    borderColor: selected
                                      ? theme.palette.primary.main
                                      : alpha(theme.palette.text.primary, 0.12),
                                    opacity: dragSnapshot.isDragging ? 0.92 : 1,
                                    ...dragProvided.draggableProps.style,
                                  }}
                                >
                                  <FeriaLazyThumb photo={photo} video={video} />
                                </Box>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              );
            })}
          </Box>
        </DragDropContext>
      )}

      <FeriaDeletePhotoDialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deletingPhoto) setDeleteTarget(null);
        }}
        onConfirm={confirmDeletePhoto}
        deleting={deletingPhoto}
        isVideo={isVideo(deleteTarget)}
      />
    </Stack>
  );
};

export default FeriaMediaGroupsBoard;
